import React, { useState, useEffect } from "react"

export default function GamesManager() {
  const [games, setGames] = useState([])
  const [shows, setShows] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingGame, setEditingGame] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    show_id: "",
    game_type: "",
    half: "first",
    needs_askfor: false,
    order_in_show: 1,
    what_happened: "",
    prompt_to_cultivate_askfors: "Please convert these batch of suggestions into a json array of strings",
    live_askfor: "",
    presenter: ""
  })

  useEffect(() => {
    fetchGames()
    fetchShows()
  }, [])

  const fetchGames = async () => {
    try {
      const response = await fetch("/api/admin/games")
      const data = await response.json()
      setGames(data)
      setLoading(false)
    } catch (error) {
      console.error("Error fetching games:", error)
      setLoading(false)
    }
  }

  const fetchShows = async () => {
    try {
      const response = await fetch("/api/admin/shows")
      const data = await response.json()
      setShows(data)
    } catch (error) {
      console.error("Error fetching shows:", error)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const url = editingGame
      ? `/api/admin/games/${editingGame.id}`
      : "/api/admin/games"
    const method = editingGame ? "PUT" : "POST"

    try {
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
        },
        body: JSON.stringify({ game: formData })
      })

      if (response.ok) {
        fetchGames()
        resetForm()
      } else {
        const data = await response.json()
        alert(`Error: ${data.errors?.join(", ")}`)
      }
    } catch (error) {
      console.error("Error saving game:", error)
      alert("Error saving game")
    }
  }

  const handleEdit = (game) => {
    setEditingGame(game)
    setFormData({
      show_id: game.show_id || "",
      game_type: game.game_type || "",
      half: game.half || "first",
      needs_askfor: game.needs_askfor || false,
      order_in_show: game.order_in_show || 1,
      what_happened: game.what_happened || "",
      prompt_to_cultivate_askfors: game.prompt_to_cultivate_askfors || "Please convert these batch of suggestions into a json array of strings",
      live_askfor: game.live_askfor || "",
      presenter: game.presenter || ""
    })
    setShowForm(true)
  }

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this game?")) return

    try {
      await fetch(`/api/admin/games/${id}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
        }
      })
      fetchGames()
    } catch (error) {
      console.error("Error deleting game:", error)
      alert("Error deleting game")
    }
  }

  const resetForm = () => {
    setFormData({
      show_id: "",
      game_type: "",
      half: "first",
      needs_askfor: false,
      order_in_show: 1,
      what_happened: "",
      prompt_to_cultivate_askfors: "Please convert these batch of suggestions into a json array of strings",
      live_askfor: "",
      presenter: ""
    })
    setEditingGame(null)
    setShowForm(false)
  }

  if (loading) {
    return <div className="text-center py-8">Loading...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Games Management</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-lg font-semibold"
        >
          {showForm ? "Cancel" : "+ New Game"}
        </button>
      </div>

      {showForm && (
        <div className="bg-gray-50 p-6 rounded-lg mb-6">
          <h3 className="text-lg font-semibold mb-4">
            {editingGame ? "Edit Game" : "Create New Game"}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Show *
                </label>
                <select
                  required
                  value={formData.show_id}
                  onChange={(e) =>
                    setFormData({ ...formData, show_id: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select a show</option>
                  {shows.map((show) => (
                    <option key={show.id} value={show.id}>
                      {show.title} {show.active ? "(Active)" : ""}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Game Type *
                </label>
                <input
                  type="text"
                  required
                  value={formData.game_type}
                  onChange={(e) =>
                    setFormData({ ...formData, game_type: e.target.value })
                  }
                  placeholder="e.g., Improv, Sketch, Musical"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Show Half *
                </label>
                <select
                  value={formData.half}
                  onChange={(e) =>
                    setFormData({ ...formData, half: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                >
                  <option value="first">First Half</option>
                  <option value="second">Second Half</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Order in Show *
                </label>
                <input
                  type="number"
                  required
                  min="1"
                  value={formData.order_in_show}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      order_in_show: parseInt(e.target.value)
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Presenter
                </label>
                <select
                  value={formData.presenter}
                  onChange={(e) =>
                    setFormData({ ...formData, presenter: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                >
                  <option value="">Select presenter</option>
                  <option value="Brendan">Brendan</option>
                  <option value="Kenneth">Kenneth</option>
                  <option value="Haydn">Haydn</option>
                </select>
              </div>
              <div className="flex items-center pt-6">
                <input
                  type="checkbox"
                  id="needs_askfor"
                  checked={formData.needs_askfor}
                  onChange={(e) =>
                    setFormData({ ...formData, needs_askfor: e.target.checked })
                  }
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="needs_askfor"
                  className="ml-2 block text-sm text-gray-700"
                >
                  Needs Ask For
                </label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Live Askfor
              </label>
              <input
                type="text"
                value={formData.live_askfor}
                onChange={(e) =>
                  setFormData({ ...formData, live_askfor: e.target.value })
                }
                placeholder="Enter live askfor prompt"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                What Happened (Notes)
              </label>
              <textarea
                value={formData.what_happened}
                onChange={(e) =>
                  setFormData({ ...formData, what_happened: e.target.value })
                }
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Optional notes about what happened during this game"
              />
            </div>
            {formData.needs_askfor && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ChatGPT Cultivation Prompt
                </label>
                <textarea
                  value={formData.prompt_to_cultivate_askfors}
                  onChange={(e) =>
                    setFormData({ ...formData, prompt_to_cultivate_askfors: e.target.value })
                  }
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  placeholder="Prompt for ChatGPT to format suggestions"
                />
                <p className="mt-1 text-xs text-gray-500">
                  This prompt is used by ChatGPT to convert audience suggestions into formatted items
                </p>
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="submit"
                className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-2 rounded-lg font-semibold"
              >
                {editingGame ? "Update" : "Create"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-2 rounded-lg font-semibold"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {games.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No games yet.</p>
        ) : (
          games.map((game) => (
            <div
              key={game.id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold text-gray-800">
                      {game.game_type}
                    </h3>
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-semibold rounded">
                      Order: {game.order_in_show}
                    </span>
                    <span className={`px-2 py-1 text-xs font-semibold rounded ${
                      game.half === "second"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-gray-100 text-gray-800"
                    }`}>
                      {game.half === "second" ? "2nd Half" : "1st Half"}
                    </span>
                    {game.needs_askfor && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">
                        Needs Ask For
                      </span>
                    )}
                  </div>
                  {game.what_happened && (
                    <p className="text-sm text-gray-600 mb-2">{game.what_happened}</p>
                  )}
                  {game.live_askfor && (
                    <div className="mt-2 p-2 bg-green-50 rounded">
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">Live Askfor:</span> {game.live_askfor}
                      </p>
                    </div>
                  )}
                  {game.presenter && (
                    <div className="mt-2">
                      <span className="text-sm text-gray-700">
                        <span className="font-medium">Presenter:</span> {game.presenter}
                      </span>
                    </div>
                  )}
                  {game.ask_for && (
                    <div className="mt-2 p-3 bg-yellow-50 rounded-lg">
                      <p className="text-sm font-medium text-gray-700">
                        Ask For: {game.ask_for.prompt || "(No prompt set)"}
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleEdit(game)}
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(game.id)}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-semibold"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
